import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

const PLAN_BADGE = { enterprise: 'warning text-dark', pro: 'primary', basic: 'secondary' };
const planBadge = (tier) => PLAN_BADGE[tier] || 'secondary';

const blankForm = {
  name: '', industryId: '', planId: '',
  contactEmail: '', contactPhone: '',
  adminFirstName: '', adminLastName: '',
  adminEmail: '', adminPassword: '',
};

const CATEGORY_LABELS = {
  administration: 'Administration', academics: 'Academics', learning: 'Learning',
  hrm: 'HR & People', finance: 'Finance', crm: 'CRM', operations: 'Operations',
  communication: 'Communication', reports: 'Reports',
};

export default function SATenants() {
  const [tenants, setTenants]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterPlan, setFilterPlan] = useState('');

  // Reference data
  const [industries, setIndustries] = useState([]);
  const [plans, setPlans]           = useState([]);

  // Create tenant
  const [showCreate, setShowCreate]   = useState(false);
  const [form, setForm]               = useState(blankForm);
  const [saving, setSaving]           = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  // Subscription panel
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showSubPanel, setShowSubPanel]     = useState(false);
  const [newPlanId, setNewPlanId]           = useState('');
  const [changingPlan, setChangingPlan]     = useState(false);

  // Module management panel
  const [showModulePanel,  setShowModulePanel]  = useState(false);
  const [moduleTenant,     setModuleTenant]     = useState(null);
  const [allModules,       setAllModules]        = useState([]);
  const [activeModuleIds,  setActiveModuleIds]  = useState(new Set());
  const [moduleLoading,    setModuleLoading]    = useState(false);
  const [moduleSaving,     setModuleSaving]     = useState(false);

  // Tenant detail panel
  const [detailTenant,    setDetailTenant]    = useState(null);
  const [detailLoading,   setDetailLoading]   = useState(false);
  const [detailUserCount, setDetailUserCount] = useState(null);

  // Password reset
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult,  setResetResult]  = useState(null); // { email, tempPassword }

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const loadRef = useCallback(async () => {
    const [ref1, ref2] = await Promise.all([
      api.get('/sa/industries').catch(() => ({ data: { data: [] } })),
      api.get('/sa/plans').catch(() => ({ data: { data: [] } })),
    ]);
    setIndustries(ref1.data?.data || ref1.data?.records || []);
    setPlans(ref2.data?.data || ref2.data?.records || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, search };
      if (filterIndustry) params.industryId = filterIndustry;
      if (filterPlan) params.planId = filterPlan;
      const r = await api.get('/sa/tenants', { params });
      setTenants(r.data.records || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  }, [page, search, filterIndustry, filterPlan]);

  useEffect(() => { loadRef(); }, [loadRef]);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (tenant) => {
    await api.patch(`/sa/tenants/${tenant._id}/toggle-active`);
    setToast({ show: true, message: `Tenant ${tenant.isActive ? 'suspended' : 'activated'}`, type: 'success' });
    load();
  };

  // Create tenant
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post('/sa/tenants', form);
      setCreatedInfo(r.data?.data || r.data);
      setForm(blankForm);
      setShowCreate(false);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to create tenant', type: 'danger' });
    } finally { setSaving(false); }
  };

  // Change plan
  const handleChangePlan = async () => {
    if (!newPlanId) return;
    setChangingPlan(true);
    try {
      await api.patch(`/sa/tenants/${selectedTenant._id}/change-plan`, { planId: newPlanId });
      setToast({ show: true, message: 'Plan updated!', type: 'success' });
      setShowSubPanel(false); setSelectedTenant(null);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to change plan', type: 'danger' });
    } finally { setChangingPlan(false); }
  };

  const openSubPanel = (t) => {
    setSelectedTenant(t);
    setNewPlanId(t.planId?._id || t.planId || '');
    setShowSubPanel(true);
    setShowModulePanel(false);
  };

  const openModulePanel = async (t) => {
    setModuleTenant(t);
    setShowModulePanel(true);
    setShowSubPanel(false);
    setDetailTenant(null);
    setModuleLoading(true);
    try {
      // Filter modules by tenant's industry so only relevant modules are shown
      const industryId = t.industryId?._id || t.industryId;
      const params = industryId ? { industryId, limit: 200 } : { limit: 200 };
      const r = await api.get('/sa/modules', { params });
      const mods = r.data?.data || r.data?.records || [];
      setAllModules(mods);
      // Fetch full tenant detail to get current activeModuleIds
      const tr = await api.get(`/sa/tenants/${t._id}`);
      const tenantDetail = tr.data?.data || tr.data;
      const currentIds = (tenantDetail.activeModuleIds || []).map(m => m._id || m);
      setActiveModuleIds(new Set(currentIds));
    } catch {
      setToast({ show: true, message: 'Failed to load modules', type: 'danger' });
    }
    setModuleLoading(false);
  };

  const openDetail = async (t) => {
    setShowSubPanel(false);
    setShowModulePanel(false);
    setDetailTenant(null);
    setDetailLoading(true);
    setResetResult(null);
    try {
      const r = await api.get(`/sa/tenants/${t._id}`);
      const full = r.data?.data || r.data;
      setDetailTenant(full);
      setDetailUserCount(full.userCount ?? null);
    } catch {
      setToast({ show: true, message: 'Failed to load tenant details', type: 'danger' });
    }
    setDetailLoading(false);
  };

  const handleResetPassword = async () => {
    if (!detailTenant) return;
    if (!window.confirm(`Reset the admin password for "${detailTenant.name}"? A new temporary password will be generated.`)) return;
    setResetLoading(true);
    setResetResult(null);
    try {
      const r = await api.post(`/sa/tenants/${detailTenant._id}/reset-password`);
      const d = r.data?.data || r.data;
      setResetResult(d);
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Password reset failed', type: 'danger' });
    }
    setResetLoading(false);
  };

  const toggleModule = (id) => {
    setActiveModuleIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const saveModules = async () => {
    setModuleSaving(true);
    try {
      await api.put(`/sa/tenants/${moduleTenant._id}`, { activeModuleIds: [...activeModuleIds] });
      setToast({
        show: true,
        message: `Modules updated for ${moduleTenant.name}. Newly-enabled modules were added to every role's menu access — tenant users will see them after their next page refresh.`,
        type: 'success',
      });
      setShowModulePanel(false);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed to save modules', type: 'danger' });
    }
    setModuleSaving(false);
  };

  const copyText = (text) => navigator.clipboard.writeText(text);

  // Compute whether subdomain URLs are usable:
  // - Subdomain URLs (e.g. bright-future.topskilled.in) require wildcard DNS (*.topskilled.in)
  // - If the app itself is hosted on a subdomain (e.g. tslms.topskilled.in, parts >= 3),
  //   sibling subdomains won't resolve unless wildcard DNS + nginx routing is configured.
  // - Fallback URL (?tenant=slug) always works with no extra DNS setup.
  const _h = window.location.hostname;
  const _isIP        = /^\d+\.\d+\.\d+\.\d+$/.test(_h);
  const _isLocalhost = _h === 'localhost' || _isIP;
  const _parts       = _h.split('.');
  // App is on a subdomain (e.g. tslms.topskilled.in → 3 parts) → wildcard DNS likely not set
  const _isSubdomainHosted = !_isLocalhost && _parts.length >= 3;
  // Use fallback URL when on localhost OR when the app itself is a subdomain
  const useFallbackUrl = _isLocalhost || _isSubdomainHosted;

  const platformDomain = _isLocalhost ? 'yourapp.com' : _parts.slice(-2).join('.');
  const getSubdomainUrl = (slug) => `https://${slug}.${platformDomain}/login`;
  const getFallbackUrl  = (slug) => `${window.location.origin}/login?tenant=${slug}`;
  const getRegisterUrl  = (slug) => `${window.location.origin}/register/user?tenant=${slug}`;

  return (
    <div>
      <PageHeader title="Tenants" icon="bi-buildings" subtitle={`${total} organizations`}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {/* Filters */}
            <select className="form-select form-select-sm" style={{ width: 150 }} value={filterIndustry} onChange={e => { setFilterIndustry(e.target.value); setPage(1); }}>
              <option value="">All Industries</option>
              {industries.map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 130 }} value={filterPlan} onChange={e => { setFilterPlan(e.target.value); setPage(1); }}>
              <option value="">All Plans</option>
              {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
            <div className="input-group" style={{ width: 220 }}>
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input type="text" className="form-control form-control-sm" placeholder="Search..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowCreate(true); setCreatedInfo(null); }}>
              <i className="bi bi-plus-lg me-1"></i>Add Tenant
            </button>
          </div>
        }
      />

      {/* ── Created Credentials Banner ─────────────────────────────────────── */}
      {createdInfo && (() => {
        const tSlug = createdInfo.tenant?.slug || '';
        const subUrl  = getSubdomainUrl(tSlug);
        const fbUrl   = getFallbackUrl(tSlug);
        const regUrl  = getRegisterUrl(tSlug);
        return (
          <div className="card mb-4" style={{ border: '2px solid #22c55e', borderRadius: 12 }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
              <span style={{ fontWeight: 700, color: '#15803d' }}>
                <i className="bi bi-check-circle-fill me-2"></i>
                Tenant "{createdInfo.tenant?.name}" created successfully!
              </span>
              <button className="btn-close" onClick={() => setCreatedInfo(null)}></button>
            </div>
            {createdInfo.credentials && (
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
                  Share these credentials with the tenant admin. The password is only shown once.
                </p>

                {/* Credentials row */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Admin Email</label>
                    <div className="input-group input-group-sm">
                      <input className="form-control" readOnly value={createdInfo.credentials.email} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                      <button className="btn btn-outline-secondary" onClick={() => copyText(createdInfo.credentials.email)}><i className="bi bi-clipboard"></i></button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Password <span style={{ color: '#f59e0b' }}>(shown once)</span></label>
                    <div className="input-group input-group-sm">
                      <input className="form-control" readOnly value={createdInfo.credentials.password} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                      <button className="btn btn-outline-secondary" onClick={() => copyText(createdInfo.credentials.password)}><i className="bi bi-clipboard"></i></button>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Internal Slug</label>
                    <div className="input-group input-group-sm">
                      <input className="form-control" readOnly value={tSlug} style={{ fontFamily: 'monospace', fontSize: 12, background: '#f1f5f9', color: '#6366f1', fontWeight: 600 }} />
                      <button className="btn btn-outline-secondary" onClick={() => copyText(tSlug)}><i className="bi bi-clipboard"></i></button>
                    </div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Internal use only — not for sharing with clients</div>
                  </div>
                </div>

                {/* URL section */}
                <div style={{ fontWeight: 700, fontSize: 12, color: '#374151', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e5e7eb', paddingBottom: 6, marginBottom: 12 }}>
                  URLs to Share
                </div>

                {useFallbackUrl && (
                  <div className="alert alert-info d-flex gap-2 align-items-start py-2 mb-3" style={{ fontSize: 12 }}>
                    <i className="bi bi-info-circle-fill mt-1" style={{ flexShrink: 0 }}></i>
                    <span>
                      {_isLocalhost
                        ? <>You're on <strong>localhost</strong> — subdomain URLs won't work locally. Use the <strong>Fallback URL</strong> below.</>
                        : <>Your app is hosted on a subdomain (<strong>{_h}</strong>). Subdomain tenant URLs require wildcard DNS (<code>*.{platformDomain}</code>) + nginx setup. Use the <strong>Fallback URL</strong> for now.</>
                      }
                    </span>
                  </div>
                )}

                <div className="row g-3">
                  {/* Fallback URL — always first and recommended when on subdomain hosting */}
                  <div className={`col-md-${useFallbackUrl ? '12' : '6'}`}>
                    <div className="p-3 rounded h-100" style={{ background: '#f0fdf4', border: '2px solid #bbf7d0' }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#15803d' }}>
                          <i className="bi bi-link-45deg me-1"></i>Login URL to Share
                        </div>
                        <span className="badge" style={{ background: '#22c55e', fontSize: 10 }}>
                          <i className="bi bi-check-circle-fill me-1"></i>Recommended
                        </span>
                      </div>
                      <div className="input-group input-group-sm mb-2">
                        <input className="form-control" readOnly value={fbUrl} style={{ fontFamily: 'monospace', fontSize: 11, background: '#fff', fontWeight: 600, color: '#15803d' }} />
                        <button className="btn btn-success" onClick={() => copyText(fbUrl)}><i className="bi bi-clipboard"></i></button>
                      </div>
                      <div style={{ fontSize: 11, color: '#16a34a' }}>Share this link with your tenant admin. The login page will automatically scope to their organization.</div>
                      <div className="d-flex gap-2 mt-2">
                        <a href={fbUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success btn-sm" style={{ fontSize: 11 }}>
                          <i className="bi bi-box-arrow-up-right me-1"></i>Preview
                        </a>
                        <a href={`mailto:${createdInfo.credentials.email}?subject=Welcome to ${createdInfo.tenant?.name}&body=Hi,%0A%0AWelcome to ${createdInfo.tenant?.name}!%0A%0AYour Access Links:%0ALogin URL: ${fbUrl}%0ARegistration URL (share with team members): ${regUrl}%0A%0AAdmin Credentials:%0AEmail: ${createdInfo.credentials.email}%0APassword: ${createdInfo.credentials.password}%0A%0APlease change your password after first login.%0A%0ARegards`}
                          className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}>
                          <i className="bi bi-envelope me-1"></i>Email Client
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Register URL — for tenant to share with their team members for self-signup */}
                  <div className="col-12">
                    <div className="p-3 rounded" style={{ background: '#eff6ff', border: '2px solid #bfdbfe' }}>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div style={{ fontWeight: 700, fontSize: 12, color: '#1d4ed8' }}>
                          <i className="bi bi-person-plus me-1"></i>Register URL (for team members to sign up)
                        </div>
                        <span className="badge bg-primary" style={{ fontSize: 10 }}>
                          <i className="bi bi-share me-1"></i>Tenant Self-Registration
                        </span>
                      </div>
                      <div className="input-group input-group-sm mb-2">
                        <input className="form-control" readOnly value={regUrl} style={{ fontFamily: 'monospace', fontSize: 11, background: '#fff', fontWeight: 600, color: '#1d4ed8' }} />
                        <button className="btn btn-primary" onClick={() => copyText(regUrl)}><i className="bi bi-clipboard"></i></button>
                      </div>
                      <div style={{ fontSize: 11, color: '#1e3a8a' }}>Tenant admin shares this with their team so new users can self-register under this organization.</div>
                      <div className="d-flex gap-2 mt-2">
                        <a href={regUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }}>
                          <i className="bi bi-box-arrow-up-right me-1"></i>Preview
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Subdomain URL — only shown when app is on root domain (wildcard DNS possible) */}
                  {!useFallbackUrl && (
                    <div className="col-md-6">
                      <div className="p-3 rounded h-100" style={{ background: '#fffbeb', border: '1.5px dashed #fcd34d' }}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div style={{ fontWeight: 700, fontSize: 12, color: '#92400e' }}>
                            <i className="bi bi-diagram-3 me-1"></i>Subdomain URL
                          </div>
                          <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>
                            Needs wildcard DNS
                          </span>
                        </div>
                        <div className="input-group input-group-sm mb-2">
                          <input className="form-control" readOnly value={subUrl} style={{ fontFamily: 'monospace', fontSize: 11, background: '#fff', color: '#92400e' }} />
                          <button className="btn btn-outline-warning" onClick={() => copyText(subUrl)}><i className="bi bi-clipboard"></i></button>
                        </div>
                        <div style={{ fontSize: 11, color: '#78350f' }}>Requires <code>*.{platformDomain}</code> DNS record pointing to this server.</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Create Tenant Form ────────────────────────────────────────────── */}
      {showCreate && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 14 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff', borderBottom: '1px solid #c7d2fe' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-buildings me-2 text-primary"></i>Create New Tenant
            </span>
            <button className="btn-close" onClick={() => setShowCreate(false)}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreate}>
              <div className="row g-3">

                {/* Org Info */}
                <div className="col-12">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4 }}>Organization</div>
                </div>
                <div className="col-md-5">
                  <label className="form-label">Organization Name <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Bright Future Academy" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Industry <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.industryId} onChange={e => setForm(f => ({ ...f, industryId: e.target.value }))} required>
                    <option value="">— Select industry —</option>
                    {industries.map(i => <option key={i._id} value={i._id}>{i.name}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">
                    Subscription Plan <span className="text-danger">*</span>
                    <span className="ms-1 badge bg-info" style={{ fontSize: 10 }}>Basic = free trial</span>
                  </label>
                  <select className="form-select" value={form.planId} onChange={e => setForm(f => ({ ...f, planId: e.target.value }))} required>
                    <option value="">— Select plan —</option>
                    {plans.map(p => <option key={p._id} value={p._id}>{p.name} — ₹{p.price?.monthly?.toLocaleString()}/mo</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Contact Email</label>
                  <input type="email" className="form-control" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="contact@company.com" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Contact Phone</label>
                  <input className="form-control" value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} placeholder="+91 98765 43210" />
                </div>

                {/* Admin User */}
                <div className="col-12 mt-1">
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#6366f1', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e0e7ff', paddingBottom: 4 }}>
                    Admin Account <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>(login credentials for the tenant admin)</span>
                  </div>
                </div>
                <div className="col-md-3">
                  <label className="form-label">First Name</label>
                  <input className="form-control" value={form.adminFirstName} onChange={e => setForm(f => ({ ...f, adminFirstName: e.target.value }))} placeholder="First name" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Last Name</label>
                  <input className="form-control" value={form.adminLastName} onChange={e => setForm(f => ({ ...f, adminLastName: e.target.value }))} placeholder="Last name" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Admin Email <span className="text-danger">*</span></label>
                  <input type="email" className="form-control" value={form.adminEmail} onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))} required placeholder="admin@company.com" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Password <span style={{ fontSize: 11, color: '#94a3b8' }}>(leave blank to auto-generate)</span></label>
                  <input className="form-control" value={form.adminPassword} onChange={e => setForm(f => ({ ...f, adminPassword: e.target.value }))} placeholder="Auto-generated if empty" />
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  Create Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Subscription Panel ────────────────────────────────────────────── */}
      {showSubPanel && selectedTenant && (
        <div className="card mb-4" style={{ border: '2px solid #fed7aa', borderRadius: 14 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-credit-card me-2 text-warning"></i>
              Subscription — {selectedTenant.name}
            </span>
            <button className="btn-close" onClick={() => setShowSubPanel(false)}></button>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-center">
              <div className="col-md-4">
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Current Plan</div>
                <div className="d-flex align-items-center gap-2">
                  <span className={`badge bg-${planBadge(selectedTenant.planId?.tier)}`} style={{ fontSize: 13, padding: '6px 12px' }}>
                    {selectedTenant.planId?.name || '—'}
                  </span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{selectedTenant.planId?.tier}</span>
                </div>
              </div>
              <div className="col-md-5">
                <label className="form-label" style={{ fontSize: 12 }}>Change to Plan</label>
                <select className="form-select form-select-sm" value={newPlanId} onChange={e => setNewPlanId(e.target.value)}>
                  <option value="">— Select new plan —</option>
                  {plans.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.name} ({p.tier}) — ₹{p.price?.monthly?.toLocaleString()}/mo — {p.allowedModuleIds?.length || 0} modules
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <button className="btn btn-warning btn-sm w-100" onClick={handleChangePlan} disabled={changingPlan || !newPlanId}>
                  {changingPlan && <span className="spinner-border spinner-border-sm me-1"></span>}
                  <i className="bi bi-arrow-repeat me-1"></i>Change Plan
                </button>
              </div>
              <div className="col-12">
                <div className="alert alert-info mb-0" style={{ fontSize: 12, borderRadius: 8 }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Changing the plan will immediately update the tenant's active modules to match the new plan's module set.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tenant Detail Panel ──────────────────────────────────────────── */}
      {(detailLoading || detailTenant) && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 14 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#f5f3ff', borderBottom: '1px solid #c7d2fe' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-buildings me-2 text-primary"></i>
              {detailTenant?.name || 'Loading...'}
              {detailTenant && <span className="ms-2 badge bg-light text-dark" style={{ fontSize: 11 }}>{detailTenant.industryId?.name || 'Unknown Industry'}</span>}
            </span>
            <div className="d-flex gap-2">
              {detailTenant && (
                <>
                  <button className="btn btn-outline-info btn-sm" style={{ fontSize: 12 }} onClick={() => openModulePanel(detailTenant)}>
                    <i className="bi bi-grid me-1"></i>Manage Modules
                  </button>
                  <button className="btn btn-outline-warning btn-sm" style={{ fontSize: 12 }} onClick={() => openSubPanel(detailTenant)}>
                    <i className="bi bi-credit-card me-1"></i>Change Plan
                  </button>
                  <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 12 }} onClick={handleResetPassword} disabled={resetLoading}>
                    {resetLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-key me-1"></i>}
                    Reset Password
                  </button>
                </>
              )}
              <button className="btn-close" onClick={() => setDetailTenant(null)}></button>
            </div>
          </div>
          <div className="card-body">
            {detailLoading ? (
              <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>
            ) : detailTenant && (() => {
              const t = detailTenant;
              const loginUrl = getFallbackUrl(t.slug);
              const registerUrl = getRegisterUrl(t.slug);
              const mods = t.activeModuleIds || [];
              return (
                <>
                {resetResult && (
                  <div className="alert mb-3 d-flex align-items-start gap-3" style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 10, fontSize: 13 }}>
                    <i className="bi bi-key-fill text-warning mt-1" style={{ fontSize: 18 }}></i>
                    <div>
                      <div className="fw-bold mb-1">Temporary Password Generated</div>
                      <div>Email: <strong>{resetResult.email}</strong></div>
                      <div className="d-flex align-items-center gap-2 mt-1">
                        <span>Password:</span>
                        <code style={{ background: '#fef9c3', padding: '2px 8px', borderRadius: 6, fontSize: 14, fontWeight: 700, letterSpacing: 1 }}>{resetResult.tempPassword}</code>
                        <button className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => { navigator.clipboard.writeText(resetResult.tempPassword); }}>
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>Share this with the tenant admin. They should change it after first login.</div>
                    </div>
                  </div>
                )}
                <div className="row g-3">
                  {/* Basic Info */}
                  <div className="col-md-4">
                    <div className="p-3 rounded h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10 }}>Organization</div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Name</span><div className="fw-semibold">{t.name}</div></div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Slug</span><div><code style={{ fontSize: 12, color: '#6366f1' }}>{t.slug}</code></div></div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Industry</span><div>{t.industryId?.name || '—'}</div></div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Plan</span>
                        <div><span className={`badge bg-${planBadge(t.planId?.tier)}`}>{t.planId?.name || '—'}</span></div>
                      </div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Status</span>
                        <div><span className={`badge ${t.isActive ? 'bg-success' : 'bg-danger'}`}>{t.isActive ? 'Active' : 'Suspended'}</span></div>
                      </div>
                      <div><span style={{ fontSize: 11, color: '#94a3b8' }}>Created</span><div>{new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div></div>
                    </div>
                  </div>

                  {/* Contact & Login */}
                  <div className="col-md-4">
                    <div className="p-3 rounded h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10 }}>Contact & Access</div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Email</span><div>{t.contactEmail || '—'}</div></div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Phone</span><div>{t.contactPhone || '—'}</div></div>
                      <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Website</span><div>{t.website || '—'}</div></div>
                      {(t.userCount != null) && <div className="mb-2"><span style={{ fontSize: 11, color: '#94a3b8' }}>Total Users</span><div className="fw-semibold">{t.userCount}</div></div>}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Login URL</div>
                      <div className="input-group input-group-sm mb-2">
                        <input className="form-control" readOnly value={loginUrl} style={{ fontFamily: 'monospace', fontSize: 11, color: '#15803d' }} />
                        <button className="btn btn-outline-secondary" onClick={() => copyText(loginUrl)}><i className="bi bi-clipboard"></i></button>
                        <a href={loginUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success"><i className="bi bi-box-arrow-up-right"></i></a>
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Register URL (for team self-signup)</div>
                      <div className="input-group input-group-sm">
                        <input className="form-control" readOnly value={registerUrl} style={{ fontFamily: 'monospace', fontSize: 11, color: '#1d4ed8' }} />
                        <button className="btn btn-outline-secondary" onClick={() => copyText(registerUrl)}><i className="bi bi-clipboard"></i></button>
                        <a href={registerUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary"><i className="bi bi-box-arrow-up-right"></i></a>
                      </div>
                    </div>
                  </div>

                  {/* Active Modules */}
                  <div className="col-md-4">
                    <div className="p-3 rounded h-100" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Active Modules</div>
                        <span className="badge bg-info">{mods.length}</span>
                      </div>
                      <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                        {mods.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>No modules assigned</div>
                        ) : mods.map(m => (
                          <div key={m._id || m} className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: 12 }}>
                            <i className={`${m.icon || 'bi bi-grid'} text-info`} style={{ fontSize: 13, width: 16 }}></i>
                            <span>{m.name || m.key || m}</span>
                          </div>
                        ))}
                      </div>
                      <button className="btn btn-outline-info btn-sm mt-3 w-100" style={{ fontSize: 12 }} onClick={() => openModulePanel(t)}>
                        <i className="bi bi-pencil me-1"></i>Edit Modules
                      </button>
                    </div>
                  </div>
                </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Module Management Panel ───────────────────────────────────────── */}
      {showModulePanel && moduleTenant && (
        <div className="card mb-4" style={{ border: '2px solid #a5f3fc', borderRadius: 14 }}>
          <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#ecfeff', borderBottom: '1px solid #a5f3fc' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-grid me-2 text-info"></i>
              Manage Modules — <span style={{ color: '#0e7490' }}>{moduleTenant.name}</span>
              <span className="ms-2 badge bg-info" style={{ fontSize: 11 }}>{activeModuleIds.size} active</span>
            </span>
            <div className="d-flex gap-2">
              <button className="btn btn-info btn-sm" onClick={saveModules} disabled={moduleSaving}>
                {moduleSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}
                Save Modules
              </button>
              <button className="btn-close" onClick={() => setShowModulePanel(false)}></button>
            </div>
          </div>
          <div className="card-body">
            {moduleLoading ? (
              <div className="text-center py-4"><span className="spinner-border text-info"></span></div>
            ) : (
              <>
                <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Check the modules you want active for this tenant. After saving, changes appear in the tenant's sidebar on their next page refresh — no re-login needed.
                </div>
                <div className="d-flex gap-2 mb-3">
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setActiveModuleIds(new Set(allModules.map(m => m._id)))}>Select All</button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => setActiveModuleIds(new Set())}>Deselect All</button>
                </div>
                {Object.entries(
                  allModules.reduce((acc, m) => {
                    const cat = m.category || 'other';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(m);
                    return acc;
                  }, {})
                ).map(([cat, mods]) => (
                  <div key={cat} className="mb-3">
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #e2e8f0', paddingBottom: 4, marginBottom: 8 }}>
                      {CATEGORY_LABELS[cat] || cat}
                    </div>
                    <div className="row g-2">
                      {mods.map(m => {
                        const active = activeModuleIds.has(m._id);
                        return (
                          <div className="col-md-3 col-sm-4 col-6" key={m._id}>
                            <label className="d-flex align-items-center gap-2 p-2 rounded" style={{ cursor: 'pointer', background: active ? '#ecfeff' : '#f8fafc', border: `1px solid ${active ? '#67e8f9' : '#e2e8f0'}`, borderRadius: 8, fontSize: 12, userSelect: 'none' }}>
                              <input type="checkbox" checked={active} onChange={() => toggleModule(m._id)} style={{ width: 14, height: 14, flexShrink: 0 }} />
                              <i className={`${m.icon || 'bi bi-grid'} text-info`} style={{ fontSize: 14, flexShrink: 0 }}></i>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: active ? 600 : 400 }}>{m.name}</span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Tenants Table ─────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? <LoadingSpinner /> : (
            <div className="table-responsive">
              <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Industry</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Modules</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map(t => (
                    <tr key={t._id}
                      onClick={() => openDetail(t)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view tenant details"
                    >
                      <td>
                        <div style={{ fontWeight: 600 }}>{t.name}</div>
                        <div className="d-flex align-items-center gap-1 flex-wrap">
                          <code style={{ fontSize: 10, color: '#94a3b8' }}>{t.slug}</code>
                          <span style={{ fontSize: 10, color: '#bbf7d0', background: '#052e16', padding: '1px 5px', borderRadius: 4, fontFamily: 'monospace' }}
                            title={`Subdomain: ${getSubdomainUrl(t.slug)}`}>
                            .{platformDomain}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark" style={{ fontSize: 11 }}>
                          {t.industryId?.name || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${planBadge(t.planId?.tier)}`}>
                          {t.planId?.name || '—'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${t.isActive ? 'bg-success' : 'bg-danger'}`}>
                          {t.isActive ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12, color: '#0e7490', textDecoration: 'none' }}
                          onClick={e => { e.stopPropagation(); openModulePanel(t); }} title="Manage modules">
                          <i className="bi bi-grid me-1"></i>{t.activeModuleIds?.length || 0} modules
                        </button>
                      </td>
                      <td style={{ color: '#94a3b8' }}>{new Date(t.createdAt).toLocaleDateString()}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-outline-info btn-sm"
                            style={{ padding: '3px 8px', fontSize: 11 }}
                            title="Manage modules"
                            onClick={() => openModulePanel(t)}
                          >
                            <i className="bi bi-grid"></i>
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            style={{ padding: '3px 8px', fontSize: 11 }}
                            title="Manage subscription"
                            onClick={() => openSubPanel(t)}
                          >
                            <i className="bi bi-credit-card"></i>
                          </button>
                          <a
                            href={useFallbackUrl ? getFallbackUrl(t.slug) : getSubdomainUrl(t.slug)}
                            target="_blank" rel="noreferrer"
                            className="btn btn-outline-success btn-sm"
                            style={{ padding: '3px 8px', fontSize: 11 }}
                            title={`Open tenant login: ${useFallbackUrl ? getFallbackUrl(t.slug) : getSubdomainUrl(t.slug)}`}
                          >
                            <i className="bi bi-link-45deg"></i>
                          </a>
                          <a
                            href={getRegisterUrl(t.slug)}
                            target="_blank" rel="noreferrer"
                            className="btn btn-outline-primary btn-sm"
                            style={{ padding: '3px 8px', fontSize: 11 }}
                            title={`Tenant registration URL: ${getRegisterUrl(t.slug)}`}
                          >
                            <i className="bi bi-person-plus"></i>
                          </a>
                          <button
                            className={`btn btn-sm ${t.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
                            style={{ padding: '3px 8px', fontSize: 11 }}
                            onClick={e => { e.stopPropagation(); toggleActive(t); }}
                            title={t.isActive ? 'Suspend tenant' : 'Activate tenant'}
                          >
                            <i className={`bi ${t.isActive ? 'bi-pause-circle' : 'bi-play-circle'}`}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tenants.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-5 text-muted">No tenants found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
